import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, ChevronDown, Coins, Share2, CheckCircle2, Clock, Flag, Users } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { refreshSubscriptionIfGranted } from "@/lib/push";
import { SponsorAd } from "@/components/SponsorAd";
import { InterestBanner } from "@/components/InterestBanner";

const CYAN = "#2dd4bf";
const REGISTER = "#ff6a2b";           // distinct warm CTA - stands apart from the cyan accents
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const LOGO_DARK = "https://customer-assets-jai6qajn.emergentagent.net/job_type-race-4/artifacts/ne0g788w_Text-RAcing-04-02-e1599230884415.png";
// Shared even-height pill so all event-card action buttons line up beside each other.
const BTN = "h-11 px-6 rounded-full text-sm font-bold uppercase tracking-wide inline-flex items-center justify-center gap-1.5 transition-transform hover:scale-[1.03]";
const RACE_WINDOW_MS = 15 * 60 * 1000; // sprint races finish in minutes; same window as admin Manage Contests

// upcoming | live | completed | cancelled | ended
function raceState(c, now = Date.now()) {
  if (c.status === "cancelled") return "cancelled";
  if (c.status === "completed") return "completed";
  const start = dayjs(c.start_time).valueOf();
  if (now < start) return "upcoming";
  if (c.status === "hold") return "ended";   // held by Race Logic - it never runs, so never "live"
  if (c.status === "racing" || c.status === "live") return now - start <= 3 * 3600 * 1000 ? "live" : "ended";
  if (now - start <= RACE_WINDOW_MS) return "live";
  return "ended";
}
const isOver = (state) => state === "completed" || state === "cancelled" || state === "ended";

// Never render a blank title for legacy contests saved without a name.
function raceTitle(c) {
  const n = (c?.name || "").trim();
  if (n) return n;
  const type = c?.race_type ? c.race_type.charAt(0).toUpperCase() + c.race_type.slice(1) : "Text";
  return `Untitled ${type} Race`;
}

const TZ_ABBR = (() => {
  try {
    return new Intl.DateTimeFormat("en-US", { timeZoneName: "short" })
      .formatToParts(new Date()).find((p) => p.type === "timeZoneName")?.value || "";
  } catch { return ""; }
})();

function useNow(intervalMs = 1000) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

// Polls how many racers currently have the Start-Line open, while a race is live.
function LiveRacerCount({ contestId, active }) {
  const [n, setN] = useState(null);
  useEffect(() => {
    if (!active) { setN(null); return; }
    let alive = true;
    const fetchIt = async () => {
      try { const { data } = await api.get(`/contests/${contestId}/live`); if (alive) setN(data.live); }
      catch { /* ignore transient errors */ }
    };
    fetchIt();
    const id = setInterval(fetchIt, 8000);
    return () => { alive = false; clearInterval(id); };
  }, [contestId, active]);
  if (!active || n === null) return null;
  return (
    <span className="flex items-center gap-1.5 text-xs md:text-sm font-bold px-3 py-1 rounded-full ml-auto"
      style={{ background: "#ffffff22", color: "#fff" }} data-testid={`live-count-${contestId}`}>
      <Users size={14} /> {n} on the Start-Line
    </span>
  );
}

function Countdown({ start, status }) {
  const now = useNow(1000);
  const diff = dayjs(start).valueOf() - now;
  const pill = "tabular text-base md:text-lg font-bold px-3 py-1 rounded-full inline-flex items-center";
  if (status === "cancelled") {
    return <span className={pill} style={{ color: "#f87171", background: "#f8717118" }} data-testid="countdown">CANCELLED</span>;
  }
  if (diff <= 0) {
    const ago = Math.abs(diff);
    const label = ago < 90 * 60 * 1000 ? "IN PROGRESS" : "STARTED";
    const liveNow = ago < 90 * 60 * 1000;
    return <span className={pill} style={{ color: liveNow ? "#f87171" : CYAN, background: liveNow ? "#f8717118" : `${CYAN}14` }} data-testid="countdown">{label}</span>;
  }
  const s = Math.floor(diff / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  let parts;
  if (d > 0) parts = `${d}d ${h}h`;
  else if (h > 0) parts = `${h}h ${String(m).padStart(2, "0")}m`;
  else parts = `${m}m ${String(sec).padStart(2, "0")}s`;
  const soon = diff < 5 * 60 * 1000;
  return (
    <span className={pill} style={{ color: soon ? "#fbbf24" : CYAN, background: soon ? "#fbbf2418" : `${CYAN}14` }} data-testid="countdown">
      STARTS IN {parts}
    </span>
  );
}


function FlyerThumb({ c, size = "md" }) {
  const dims = size === "sm" ? "h-12 w-12" : "h-24 w-24 md:h-28 md:w-28";
  const src = c.flyer_image_url ? `${BACKEND}${c.flyer_image_url}` : null;
  return (
    <div className={`${dims} shrink-0 relative overflow-hidden rounded-sm bg-white flex items-center justify-center border border-black/10`}>
      {src
        ? <img src={src} alt="Event flyer" className="w-full h-full object-cover" />
        : <img src={LOGO_DARK} alt="Text Racing" className="w-4/5 h-auto object-contain" data-testid="flyer-logo-fallback" />}
    </div>
  );
}

function deriveTabs(c) {
  const prize = c.prize_pool_credits > 0 ? `$${(c.prize_pool_credits / 100).toFixed(2)} prize pool` : "Sponsored / non-cash";
  return {
    GAMEPLAY: [
      `Format: ${c.race_type}`,
      `Length: ${c.length_chars} characters`,
      `Track type: ${c.track_type}`,
      `Transmission: ${c.transmission} keyboard buttons`,
    ],
    RULES: [
      "No delete key. Mistakes cannot be corrected.",
      `Each incorrect keystroke adds ${c.penalty_seconds}s to your time.`,
      "Registration stays open until the race starts.",
      "Server-verified results with anti-cheat enforcement.",
    ],
    ELIGIBILITY: [
      c.invitation === "all" ? "Open to all members." : "Member-specific invitation.",
      "BR License in good standing required.",
      "Must be 18+ to enter cash races.",
    ],
    PRIZE: [
      prize,
      c.podium_size === 1 ? "Winner takes all." : `Top ${c.podium_size} finishers paid.`,
      c.entry_fee_credits > 0 ? `Entry fee: ${c.entry_fee_credits} credits.` : "Free entry.",
    ],
  };
}

function EventCard({ c, onRegister, registered, defaultOpen = false, onClose }) {
  const [open, setOpen] = useState(defaultOpen);
  const [tab, setTab] = useState("GAMEPLAY");
  const tabs = useMemo(() => deriveTabs(c), [c]);
  const now = useNow(1000);
  const d = dayjs(c.start_time);
  const state = raceState(c, now);
  const over = isOver(state);
  const live = state === "live";
  const accent = over ? "#3f3f46" : live ? "#f87171" : CYAN;

  const shareFlyer = async () => {
    const url = `${window.location.origin}/flyer/${c.id}`;
    const payload = {
      title: `${raceTitle(c)} - Text Racing`,
      text: `Join me for ${raceTitle(c)} on Text Racing! ${d.format("ddd, MMM D · h:mm A")}`,
      url,
    };
    if (navigator.share) {
      try { await navigator.share(payload); } catch (e) { /* user dismissed */ }
    } else {
      try { await navigator.clipboard.writeText(url); toast.success("Flyer link copied - paste it anywhere to share"); }
      catch { window.open(url, "_blank"); }
    }
  };

  return (
    <div className="relative border rounded-sm overflow-hidden transition-all" style={{ borderColor: `${accent}55`, background: "#0d0d0f", opacity: over ? 0.62 : 1, filter: over ? "grayscale(0.85)" : "none" }}
      data-testid={`event-card-${c.id}`} data-state={state}>
      <div className="h-1 w-full" style={{ background: accent }} />
      {live && (
        <div className="flex items-center gap-2 px-4 py-1.5 text-[11px] font-mono uppercase tracking-widest text-white" style={{ background: "#f8717122" }} data-testid={`live-banner-${c.id}`}>
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full rounded-full animate-ping" style={{ background: "#f87171" }} />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ background: "#f87171" }} />
          </span>
          Race in progress - live now
          <LiveRacerCount contestId={c.id} active={live} />
        </div>
      )}
      <div className="p-4 flex gap-4">
        <FlyerThumb c={c} />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="font-mono text-base md:text-lg text-white truncate">{raceTitle(c)}</h3>
              <span className="text-[10px] font-mono uppercase tracking-[0.2em]" style={{ color: accent }}>{c.race_type}</span>
            </div>
            <div className="text-right shrink-0">
              <div className="font-heading text-2xl leading-none tracking-tight text-white">{d.format("ddd, MMM D").toUpperCase()}</div>
              <div className="tabular text-sm text-white/90 mt-1 flex items-center gap-1 justify-end font-semibold">
                <Clock size={12} /> {d.format("h:mm A")} {TZ_ABBR}
              </div>
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
            <Countdown start={c.start_time} status={c.status} />
            <button onClick={() => setOpen((o) => !o)} data-testid={`event-toggle-${c.id}`}
              className="flex items-center gap-1.5 text-sm font-semibold text-white bg-white/10 hover:bg-white/20 border border-white/15 rounded-full px-4 py-1.5 transition-colors">
              Details <ChevronDown size={15} className={`transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="px-4 pb-3">
          <div className="flex flex-wrap gap-1.5 mb-3">
            {Object.keys(tabs).map((t) => (
              <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t.toLowerCase()}-${c.id}`}
                className="px-3 py-1.5 text-[11px] font-mono uppercase tracking-wide border rounded-sm transition-colors"
                style={tab === t
                  ? { background: CYAN, color: "#04120f", borderColor: CYAN }
                  : { color: "#a1a1aa", borderColor: "#2a2a2e" }}>
                {t}
              </button>
            ))}
          </div>
          <ul className="space-y-1.5 mb-4">
            {tabs[tab].map((line, i) => (
              <li key={i} className="text-sm text-white/80 flex gap-2">
                <span style={{ color: CYAN }}>›</span> {line}
              </li>
            ))}
          </ul>
          {/* Even, same-level action buttons sitting beside each other */}
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={() => (onClose ? onClose() : setOpen(false))} data-testid={`close-${c.id}`}
              className={`${BTN} border text-white hover:border-white`} style={{ borderColor: "#2a2a2e" }}>Close</button>
            {state === "completed" ? (
              <Link to={`/results/${c.id}`} data-testid={`results-${c.id}`}
                className={BTN} style={{ background: CYAN, color: "#04120f" }}>Final Results</Link>
            ) : registered && (state === "upcoming" || live) ? (
              <Link to={`/lobby/${c.id}`} data-testid={`enter-${c.id}`}
                className={BTN} style={{ background: live ? "#f87171" : CYAN, color: "#04120f" }}>
                {live && <Flag size={13} />}{live ? "Enter Live" : "Enter"}</Link>
            ) : live ? (
              <span data-testid={`inprogress-${c.id}`} className={`${BTN} border`} style={{ borderColor: "#f8717155", color: "#f87171" }}>Race In Progress</span>
            ) : state === "ended" ? (
              <span data-testid={`ended-${c.id}`} className={`${BTN} border text-race-muted`} style={{ borderColor: "#2a2a2e" }}>Race Ended</span>
            ) : state === "cancelled" ? (
              <span data-testid={`cancelled-${c.id}`} className={`${BTN} border`} style={{ borderColor: "#f8717155", color: "#f87171" }}>Cancelled</span>
            ) : (
              <button onClick={() => onRegister(c)} data-testid={`register-${c.id}`}
                className={BTN} style={{ background: REGISTER, color: "#fff", boxShadow: `0 6px 22px ${REGISTER}66` }}>Register</button>
            )}
            <button onClick={shareFlyer} data-testid={`flyer-${c.id}`} title="Share event flyer"
              className={BTN} style={{ background: CYAN, color: "#04120f", boxShadow: `0 4px 16px ${CYAN}55` }}>
              <Share2 size={16} /> Share
            </button>
          </div>
        </div>
      )}

      {/* Status pushed to the far bottom-right, out of the way */}
      <div className="px-4 pb-2 flex justify-end">
        <span className={`flex items-center gap-1 text-[10px] font-mono uppercase tracking-wide px-2 py-0.5 rounded-full ${state === "confirmed" ? "text-race-volt bg-race-volt/10" : over ? "text-race-muted bg-white/5" : live ? "text-[#f87171] bg-[#f8717118]" : "text-white/60 bg-white/5"}`} data-testid={`status-${c.id}`}>
          <CheckCircle2 size={11} /> {c.status}
        </span>
      </div>
    </div>
  );
}

/* ---------- WEEK ---------- */
function WeekView({ contests, onSelectDay }) {
  const [cursor, setCursor] = useState(dayjs());
  const start = cursor.startOf("week");
  const days = Array.from({ length: 7 }, (_, i) => start.add(i, "day"));
  const byDay = (day) => contests.filter((c) => dayjs(c.start_time).isSame(day, "day"));

  return (
    <div data-testid="week-view">
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => setCursor(cursor.subtract(1, "week"))} data-testid="week-prev" className="text-race-sub hover:text-white"><ChevronLeft /></button>
        <span className="font-heading text-2xl tracking-tight">{start.format("MMM D")} – {start.add(6, "day").format("MMM D")}</span>
        <button onClick={() => setCursor(cursor.add(1, "week"))} data-testid="week-next" className="text-race-sub hover:text-white"><ChevronRight /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
        {days.map((day) => {
          const evs = byDay(day);
          const isToday = day.isSame(dayjs(), "day");
          return (
            <div key={day.format()} className="border rounded-sm p-2 min-h-[92px]"
              style={{ borderColor: isToday ? CYAN : "#2a2a2e", background: "#0d0d0f" }}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-mono uppercase tracking-widest text-race-sub">{day.format("ddd")}</span>
                <span className={`tabular text-sm ${isToday ? "" : "text-race-sub"}`} style={isToday ? { color: CYAN } : {}}>{day.format("D")}</span>
              </div>
              <div className="space-y-1.5">
                {evs.map((c) => {
                  const over = isOver(raceState(c));
                  const liveEv = raceState(c) === "live";
                  return (
                  <button key={c.id} onClick={() => onSelectDay(c)} data-testid={`week-event-${c.id}`}
                    className="w-full text-left px-2 py-1.5 rounded-sm text-[11px] font-mono truncate"
                    style={{ background: liveEv ? "#f8717122" : `${CYAN}1a`, color: over ? "#71717a" : "#e5e5e5", borderLeft: `2px solid ${liveEv ? "#f87171" : over ? "#3f3f46" : CYAN}`, opacity: over ? 0.6 : 1 }}>
                    {liveEv && <span className="mr-1" style={{ color: "#f87171" }}>●</span>}{dayjs(c.start_time).format("h:mma")} {raceTitle(c).replace("www.", "").replace(".com", "")}
                  </button>
                  );
                })}
                {evs.length === 0 && <div className="text-[10px] text-race-muted">-</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- MONTH ---------- */
function MonthView({ contests, onSelectDay }) {
  const [cursor, setCursor] = useState(dayjs());
  const [selected, setSelected] = useState(dayjs());
  const startOfMonth = cursor.startOf("month");
  const startBlank = startOfMonth.day();
  const daysInMonth = cursor.daysInMonth();
  const cells = [...Array(startBlank).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => startOfMonth.add(i, "day"))];
  const byDay = (day) => contests.filter((c) => dayjs(c.start_time).isSame(day, "day"));
  const selEvents = selected ? byDay(selected) : [];

  return (
    <div data-testid="month-view">
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => setCursor(cursor.subtract(1, "month"))} data-testid="month-prev" className="text-race-sub hover:text-white"><ChevronLeft /></button>
        <span className="font-heading text-2xl tracking-tight uppercase">{cursor.format("MMMM YYYY")}</span>
        <button onClick={() => setCursor(cursor.add(1, "month"))} data-testid="month-next" className="text-race-sub hover:text-white"><ChevronRight /></button>
      </div>
      <div className="grid grid-cols-7 gap-px mb-1">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <div key={i} className="text-center text-[10px] font-mono uppercase text-race-muted py-1">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-px" style={{ background: "#2a2a2e" }}>
        {cells.map((day, i) => {
          if (!day) return <div key={i} style={{ background: "#0d0d0f" }} className="aspect-square" />;
          const evs = byDay(day);
          const isSel = selected && day.isSame(selected, "day");
          const isToday = day.isSame(dayjs(), "day");
          return (
            <button key={i} onClick={() => setSelected(day)} data-testid={`month-day-${day.format("YYYY-MM-DD")}`}
              className="aspect-square p-1.5 flex flex-col items-center justify-start transition-colors"
              style={{ background: isSel ? `${CYAN}22` : "#0d0d0f", outline: isToday ? `1px solid ${CYAN}` : "none" }}>
              <span className={`tabular text-xs ${isToday ? "" : "text-white"}`} style={isToday ? { color: CYAN } : {}}>{day.format("D")}</span>
              <div className="flex gap-0.5 mt-1 flex-wrap justify-center">
                {evs.slice(0, 3).map((c) => {
                  const st = raceState(c);
                  const dot = st === "live" ? "#f87171" : st === "confirmed" || st === "upcoming" ? CYAN : "#52525b";
                  return <span key={c.id} className="w-1.5 h-1.5 rounded-full" style={{ background: dot }} />;
                })}
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-5">
        <div className="overline mb-2">{selected ? selected.format("dddd, MMM D") : "Select a day"}</div>
        {selEvents.length === 0 ? (
          <p className="text-sm text-race-sub">No races scheduled this day.</p>
        ) : (
          <div className="space-y-2">
            {selEvents.map((c) => {
              const st = raceState(c);
              const over = isOver(st);
              const liveEv = st === "live";
              return (
              <button key={c.id} onClick={() => onSelectDay(c)} data-testid={`month-event-${c.id}`}
                className="w-full flex items-center justify-between border rounded-sm px-3 py-2.5 text-left hover:border-white transition-colors"
                style={{ borderColor: liveEv ? "#f8717155" : "#2a2a2e", background: "#0d0d0f", opacity: over ? 0.6 : 1 }}>
                <span className="font-mono text-sm truncate flex items-center gap-1.5" style={{ color: over ? "#71717a" : "#fff" }}>
                  {liveEv && <span style={{ color: "#f87171" }}>●</span>}{raceTitle(c)}
                </span>
                <span className="tabular text-xs text-white/85 flex items-center gap-2">
                  {dayjs(c.start_time).format("h:mm A")} {TZ_ABBR}
                </span>
              </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Calendar() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [contests, setContests] = useState([]);
  const [view, setView] = useState("list");
  const [registered, setRegistered] = useState(new Set());
  const [detail, setDetail] = useState(null); // contest selected from week/month -> expand in a modal-ish list
  const [creditPrompt, setCreditPrompt] = useState(null); // contest needing more credits

  const load = () => api.get("/contests").then((r) => {
    const visible = r.data.filter((c) => c.status !== "refunded");
    setContests(visible);
    setRegistered(new Set(visible.filter((c) => c.is_registered).map((c) => c.id)));
    return visible;
  }).catch(() => []);
  useEffect(() => { load(); }, []);

  // The main league calendar is forward-looking: only live + upcoming races, soonest first
  // (live pins to the top). Past/ended/cancelled races live on each member's Pit Wall.
  const now = useNow(30000);
  const upcoming = useMemo(() =>
    contests
      .filter((c) => !isOver(raceState(c, now)))
      .sort((a, b) => dayjs(a.start_time).valueOf() - dayjs(b.start_time).valueOf()),
  [contests, now]);

  // Deep-link from a scanned flyer QR: /calendar?event=<id> opens that event.
  useEffect(() => {
    const eid = params.get("event");
    if (eid && contests.length) {
      const c = contests.find((x) => x.id === eid);
      if (c) { setDetail(c); const p = new URLSearchParams(params); p.delete("event"); setParams(p, { replace: true }); }
    }
    // eslint-disable-next-line
  }, [contests]);

  const register = async (c) => {
    try {
      await api.post(`/contests/${c.id}/register`);
      toast.success(`You're in - ${c.name}. We'll notify you before the green flag.`);
      refreshSubscriptionIfGranted();
      setRegistered((s) => new Set(s).add(c.id));
      refreshUser();
      load();
    } catch (e) {
      const status = e.response?.status;
      if (status === 402) { setCreditPrompt(c); return; }
      if (status === 403) { navigate("/license?next=/calendar"); toast.error("Activate your BR License to enter."); return; }
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const views = [["list", "LIST"], ["week", "WEEK"], ["month", "MONTH"]];

  return (
    <div className="animate-slide-up">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <h1 className="font-heading text-5xl tracking-tight">CALENDAR</h1>
        <div className="flex gap-1 border rounded-sm p-1" style={{ borderColor: "#2a2a2e" }}>
          {views.map(([v, l]) => (
            <button key={v} onClick={() => setView(v)} data-testid={`calendar-view-${v}`}
              className="px-4 py-1.5 text-xs font-mono uppercase tracking-wide rounded-sm transition-colors"
              style={view === v ? { background: CYAN, color: "#04120f" } : { color: "#a1a1aa" }}>{l}</button>
          ))}
        </div>
      </div>

      {/* One ad on screen at a time: hidden (kept mounted) while the detail modal shows its own ad */}
      <div className={detail ? "invisible" : ""} data-testid="calendar-ad-wrap"><SponsorAd placement="calendar" className="mb-6" /></div>

      <InterestBanner />

      {!user?.has_license && (
        <div className="mb-5 text-sm border rounded-sm p-3" style={{ borderColor: `${CYAN}55`, color: "#a1a1aa" }} data-testid="calendar-license-note">
          Claim your <Link to="/license" className="underline" style={{ color: CYAN }}>BR License</Link> to register for these races.
        </div>
      )}

      {view === "list" && (
        <div className="space-y-4" data-testid="list-view">
          {upcoming.length === 0 ? (
            <div className="border rounded-sm p-8 text-center" style={{ borderColor: "#2a2a2e", background: "#0d0d0f" }} data-testid="calendar-empty">
              <p className="text-white font-mono mb-1">No upcoming races on the grid right now.</p>
              <p className="text-race-sub text-sm">New contests drop here first - check back soon. Auditing a race you ran? Find it on your <Link to="/dashboard" className="underline" style={{ color: CYAN }}>Pit Wall → Previous Races</Link>.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="text-xs font-mono uppercase tracking-widest text-race-sub" data-testid="calendar-upcoming-count">
                  {upcoming.length} upcoming {upcoming.length === 1 ? "race" : "races"} · next up first
                </span>
                <Link to="/dashboard" className="text-xs font-mono uppercase tracking-wide text-race-sub hover:text-white transition-colors">Past races on your Pit Wall →</Link>
              </div>
              {upcoming.map((c) => (
                <EventCard key={c.id} c={c} onRegister={register} registered={registered.has(c.id)} />
              ))}
            </>
          )}
        </div>
      )}
      {view === "week" && <WeekView contests={upcoming} onSelectDay={setDetail} />}
      {view === "month" && <MonthView contests={upcoming} onSelectDay={setDetail} />}

      {detail && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-end md:items-center justify-center p-4" onClick={() => setDetail(null)} data-testid="calendar-detail-modal">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg">
            <EventCard c={detail} onRegister={register} registered={registered.has(detail.id)} defaultOpen onClose={() => setDetail(null)} />
            <SponsorAd placement="registration" className="mt-3" />
            <button onClick={() => setDetail(null)} className="mt-3 w-full text-center text-sm text-race-sub hover:text-white">Dismiss</button>
          </div>
        </div>
      )}

      {creditPrompt && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setCreditPrompt(null)} data-testid="credit-prompt-modal">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-sm bg-race-surface border border-race-line rounded-lg p-6 text-center">
            <div className="font-heading text-3xl tracking-tight text-race-red mb-2">NOT ENOUGH CREDITS</div>
            <p className="text-race-sub text-sm">
              {creditPrompt.name} needs <span className="text-white font-mono">{creditPrompt.entry_fee_credits} credits</span> to enter.
              You have <span className="text-white font-mono">{user?.credits ?? 0}</span>. Top up to join the grid.
            </p>
            <div className="flex gap-2 mt-5">
              <button onClick={() => navigate("/buy-credits")} data-testid="credit-prompt-buy"
                className="flex-1 bg-race-red hover:bg-race-redhover text-race-bg font-heading text-xl tracking-wide py-3 rounded-sm transition-colors">ADD CREDITS</button>
              <button onClick={() => setCreditPrompt(null)} className="px-4 border border-race-line text-race-sub hover:text-white rounded-sm text-sm font-mono">Later</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
